import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TranslatedTextError } from 'src/components';
import { Loading } from 'src/components/Loading';
import { StepsIndicator } from 'src/components/StepsIndicator';
import { SText } from 'src/components/SText';
import { AllowedPhoneCountries, PhoneToCountryCodeMapper } from 'src/consts';
import { getPrefixFromFullPhone } from 'src/helpers';
import { CreateClientFormContainer } from '../../containers';
import {
  inStoreAgenciesForSelectSelector,
  inStoreAgenciesSelector,
  inStoreFormReceiverSelector,
  inStoreFormStateSelector,
  inStoreSubmitDataErrorSelector,
  inStoreSubmitDataIsLoadingSelector,
  inStoreSubmitDataSuccessSelector,
  setInStoreReceiver,
  startSubmitInStoreFormData,
} from '../store';

interface InStoreReceiverProps {
  parentUrl: string;
}
export const InStoreReceiver: React.FC<InStoreReceiverProps> = ({ parentUrl }: InStoreReceiverProps): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();

  const receiverForm = useSelector(inStoreFormReceiverSelector);
  const agencies = useSelector(inStoreAgenciesForSelectSelector);
  const agenciesWithInfo = useSelector(inStoreAgenciesSelector);
  const inStoreForm = useSelector(inStoreFormStateSelector);
  const isSubmittingData = useSelector(inStoreSubmitDataIsLoadingSelector);
  const isSuccessSubmitData = useSelector(inStoreSubmitDataSuccessSelector);
  const isSubmittingError = useSelector(inStoreSubmitDataErrorSelector);

  useEffect(() => {
    if (!inStoreForm.sender.tel || !inStoreForm.toAgency) {
      history.replace(`${parentUrl}`);
    }
  }, [inStoreForm, history, parentUrl]);

  useEffect(() => {
    if (isSuccessSubmitData) {
      history.replace(`${parentUrl}/success/${isSuccessSubmitData.clientOrderId}/${isSuccessSubmitData.tel}`);
    }
  }, [isSuccessSubmitData, parentUrl, history]);

  const prefixCountry = useMemo(() => {
    if (!inStoreForm) {
      return;
    }

    if (!inStoreForm.toAgency) {
      return;
    }

    const toAgencyInfo = agenciesWithInfo.find((agency) => inStoreForm.toAgency === agency.slug);

    if (!toAgencyInfo) {
      return;
    }

    const phonePrefix = getPrefixFromFullPhone(toAgencyInfo.tel);

    if (!phonePrefix) {
      return;
    }

    const country = Object.entries(PhoneToCountryCodeMapper).find(([key, value]) => {
      return key === phonePrefix;
    });

    if (country) return country[1];
  }, [inStoreForm, agenciesWithInfo]);

  const onSubmit = useCallback(() => {
    dispatch(startSubmitInStoreFormData());
  }, [dispatch]);

  const onFieldsUpdate = useCallback(
    (data) => {
      dispatch(setInStoreReceiver({ receiver: data }));
    },
    [dispatch],
  );

  const onPreview = useCallback(() => {
    history.goBack();
  }, [history]);

  if (isSubmittingData) {
    return <Loading />;
  }

  return (
    <div className="h-screen py-3 md:h-auto md:mt-0 mt-6 w-full relative">
      <StepsIndicator totalSteps={2} active={1} />
      <SText
        label="estore-receiver.page-title"
        classNames="my-3 text-primary block text-lg text-center font-semibold"
      />
      <CreateClientFormContainer
        agencies={agencies}
        onSubmit={onSubmit}
        requiredToAgency={false}
        onFieldsUpdate={onFieldsUpdate}
        defaultValues={receiverForm ?? {}}
        onPreview={onPreview}
        isPending={isSubmittingData}
        country={(prefixCountry ?? 'ma') as typeof AllowedPhoneCountries[number]}
      >
        {!!isSubmittingError && (
          <TranslatedTextError message={isSubmittingError.message} className="block text-center mt-2" />
        )}
      </CreateClientFormContainer>
    </div>
  );
};
